import { DroneEntity, DroneStatusType } from '@src/core/features';

/** Drones page lists and carousel — only IDLE and MISSION are shown to operators. */
export const DRONES_PAGE_VISIBLE_STATUSES = [
  DroneStatusType.IDLE,
  DroneStatusType.MISSION,
] as const;

export type DronesPageVisibleStatus = (typeof DRONES_PAGE_VISIBLE_STATUSES)[number];

export const isDronesPageVisibleDrone = (drone: DroneEntity): boolean =>
  drone.status === DroneStatusType.IDLE || drone.status === DroneStatusType.MISSION;

/** IDLE in API while order/mission is active (create → takeoff) or optimistic bridge after takeoff. */
export const isDroneInMissionPipeline = (
  drone: DroneEntity,
  optimisticMissionSerials?: string[],
  endedMissionSerials?: string[],
): boolean => {
  // Mission just ended for this serial — force idle until REST reflects it, even if the list
  // still returns MISSION/currentOrderId (lag), so the route does not revive.
  if (endedMissionSerials?.includes(drone.serialNumber)) return false;
  // A fresh optimistic create/takeoff for this serial always shows as on-mission.
  if (optimisticMissionSerials?.includes(drone.serialNumber)) return true;
  if (drone.status === DroneStatusType.MISSION) return true;
  const orderId = drone.currentOrderId;
  return orderId != null && Number(orderId) !== 0;
};

export const partitionDronesPageDrones = (
  drones: DroneEntity[],
  optimisticMissionSerials?: string[],
  endedMissionSerials?: string[],
) => {
  const visibleDrones = drones.filter(isDronesPageVisibleDrone);

  return {
    visibleDrones,
    idleDrones: visibleDrones.filter(
      (d) => !isDroneInMissionPipeline(d, optimisticMissionSerials, endedMissionSerials),
    ),
    missionDrones: visibleDrones.filter((d) =>
      isDroneInMissionPipeline(d, optimisticMissionSerials, endedMissionSerials),
    ),
  };
};
